import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DisplayLabel } from '../../../../../shared/models/displayLabel';
import { DecohabitationStatus } from '../../../../../shared/models/enums/decohabitation-status.enum';
import { DomiciliationType, DomiciliationTypeLabel } from '../../../../../shared/models/enums/domiciliation-type.enum';
import { Nationality, labelsByNationality } from '../../../../../shared/models/enums/nationality.enum';
import { ResidentialSituationDuration } from '../../../../../shared/models/enums/residential-situation-duration.enum';
import { ResidentialStatus, ResidentialStatusLabel } from '../../../../../shared/models/enums/residential-status.enum';
import { RsjCredentialType, RsjCredentialTypeLabel, credentialTypesByNationality } from '../../../../../shared/models/enums/rsj-credential-type.enum';
import { QPVService } from '../../../../../shared/services/qpv.service.ts';
import { DateValidators } from '../../../../../shared/validators/date.validators';
import { BeneficiaryDto } from '../../../../dto/beneficiary.dto';
import { RsjInstructionCivilStatusDto } from '../../dto/rsj-instruction-civil-status.dto';
import { Utils } from '../../../../../shared/utils';

@Component({
  selector: 'app-rsj-instruction-civil-status',
  templateUrl: './rsj-instruction-civil-status.component.html',
})
export class RsjInstructionCivilStatusComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public civilStatusForm: UntypedFormGroup;
  public updated: boolean = false;
  public submitted: boolean = false;

  public filteredCredentialTypes: RsjCredentialType[] = [];
  public readonly RsjCredentialType = RsjCredentialType;
  public readonly RsjCredentialTypeLabel = RsjCredentialTypeLabel;
  public readonly nationalities: Map<Nationality, string> = labelsByNationality;
  public readonly ResidentialStatusLabel = ResidentialStatusLabel;
  public readonly DomiciliationType = DomiciliationType;
  public readonly DomiciliationTypeLabel = DomiciliationTypeLabel;
  public readonly DecohabitationStatus = DecohabitationStatus;
  public readonly ResidentialSituationDuration = ResidentialSituationDuration;

  public residentialStatusCategory?: 'HostedByAThird' | 'HostedInFosterCare' | 'TenantOwner' | 'Homeless';
  public domiciliationTypeCategory?: 'CCASAssociation' | 'Other';

  public qpvs: DisplayLabel[] = [];

  public maxFileSize: number = environment.UPLOAD_MAX_FILESIZE ?? 1000 * 1000 * 1.2;
  public maxFileSizeStr: string = Utils.humanFileSize(this.maxFileSize, true, 2);

  @Input() public rsjCivilStatus: RsjInstructionCivilStatusDto;
  @Input() public beneficiary: BeneficiaryDto;
  @Input() public disabled: boolean;

  constructor(private readonly formBuilder: UntypedFormBuilder, private readonly qpvService: QPVService) {}

  public async ngOnInit(): Promise<void> {
    await this.initList();
    this.initForm();
    this.initValueChangeHandlers();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get residentialAddressForm(): UntypedFormGroup {
    return this.civilStatusForm.controls.residentialAddressForm as UntypedFormGroup;
  }

  public get postalAddressForm(): UntypedFormGroup {
    return this.civilStatusForm.controls.postalAddressForm as UntypedFormGroup;
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.civilStatusForm.valid;
  }

  public getDto(): RsjInstructionCivilStatusDto {
    if (this.civilStatusForm.valid) {
      this.rsjCivilStatus = {
        title: this.civilStatusForm.controls.title.value,
        surname: this.civilStatusForm.controls.surname.value,
        usualName: this.civilStatusForm.controls.usualName.value,
        firstName: this.civilStatusForm.controls.firstName.value,
        birthDate: this.civilStatusForm.controls.birthDate.value,
        ageYears: this.civilStatusForm.controls.ageYears.value,
        ageMonths: this.civilStatusForm.controls.ageMonths.value,
        nationality: this.civilStatusForm.controls.nationality.value,
        credentialType: this.civilStatusForm.controls.credentialType.value,
        credentialDocument1: this.civilStatusForm.controls.credentialDocument1.value,
        credentialDocumentDate1: this.civilStatusForm.controls.credentialDocumentDate1.value,
        credentialDocument2: this.civilStatusForm.controls.credentialDocument2.value,
        credentialDocumentDate2: this.civilStatusForm.controls.credentialDocumentDate2.value,
        credentialDocument3: this.civilStatusForm.controls.credentialDocument3.value,
        credentialDocumentDate3: this.civilStatusForm.controls.credentialDocumentDate3.value,
        residentialAddress: {
          id: this.residentialAddressForm.controls.id.value,
          beginDate: this.residentialAddressForm.controls.beginDate.value,
          residentialStatus: this.residentialAddressForm.controls.residentialStatus.value,
          residentialStatusComplement: this.residentialAddressForm.controls.residentialStatusComplement.value,
          address: this.residentialAddressForm.controls.address.value,
          additionalAddress: this.residentialAddressForm.controls.additionalAddress.value,
          city: this.residentialAddressForm.controls.city.value,
          postalCode: this.residentialAddressForm.controls.postalCode.value,
          qpvId: this.residentialAddressForm.controls.qpvId.value,
        },
        postalAddress: {
          id: this.postalAddressForm.controls.id.value,
          beginDate: this.postalAddressForm.controls.beginDate.value,
          domiciliationType: this.postalAddressForm.controls.domiciliationType.value,
          domiciliationTypeComplement: this.postalAddressForm.controls.domiciliationTypeComplement.value,
          address: this.postalAddressForm.controls.address.value,
          additionalAddress: this.postalAddressForm.controls.additionalAddress.value,
          city: this.postalAddressForm.controls.city.value,
          postalCode: this.postalAddressForm.controls.postalCode.value,
        },
        addressDocument1: this.civilStatusForm.controls.addressDocument1.value,
        addressDocument2: this.civilStatusForm.controls.addressDocument2.value,
        sixMonthsResident: this.civilStatusForm.controls.sixMonthsResident.value || false,
        hostedFreeOfCharge: this.civilStatusForm.controls.hostedFreeOfCharge.value,
        decohabitationStatus: this.civilStatusForm.controls.decohabitationStatus.value,
        shelterRegistration: this.civilStatusForm.controls.shelterRegistration.value,
        residentialSituationDuration: this.civilStatusForm.controls.residentialSituationDuration.value,
        searchingForHousing: this.civilStatusForm.controls.searchingForHousing.value,
        housingProcessStarted: this.civilStatusForm.controls.housingProcessStarted.value,
        housingProcessWithHelp: this.civilStatusForm.controls.housingProcessWithHelp.value,
        housingProcessWithHelpBy: this.civilStatusForm.controls.housingProcessWithHelpBy.value,
      } as RsjInstructionCivilStatusDto;
    }
    return this.rsjCivilStatus;
  }

  public syncForm(syncCivilStatus: RsjInstructionCivilStatusDto): void {
    this.updated = true;
    this.civilStatusForm.controls.title.setValue(syncCivilStatus.title);
    this.civilStatusForm.controls.surname.setValue(syncCivilStatus.surname);
    this.civilStatusForm.controls.usualName.setValue(syncCivilStatus.usualName);
    this.civilStatusForm.controls.firstName.setValue(syncCivilStatus.firstName);
    this.civilStatusForm.controls.birthDate.setValue(syncCivilStatus.birthDate);
    this.civilStatusForm.controls.ageYears.setValue(syncCivilStatus.ageYears);
    this.civilStatusForm.controls.ageMonths.setValue(syncCivilStatus.ageMonths);
    this.civilStatusForm.controls.nationality.setValue(syncCivilStatus.nationality);
    this.residentialAddressForm.controls.beginDate.setValue(syncCivilStatus.residentialAddress?.beginDate);
    this.residentialAddressForm.controls.residentialStatus.setValue(syncCivilStatus.residentialAddress?.residentialStatus);
    this.residentialAddressForm.controls.residentialStatusComplement.setValue(syncCivilStatus.residentialAddress?.residentialStatusComplement);
    this.residentialAddressForm.controls.address.setValue(syncCivilStatus.residentialAddress?.address);
    this.residentialAddressForm.controls.additionalAddress.setValue(syncCivilStatus.residentialAddress?.additionalAddress);
    this.residentialAddressForm.controls.city.setValue(syncCivilStatus.residentialAddress?.city);
    this.residentialAddressForm.controls.postalCode.setValue(syncCivilStatus.residentialAddress?.postalCode);
    this.residentialAddressForm.controls.qpvId.setValue(syncCivilStatus.residentialAddress?.qpvId);
    this.postalAddressForm.controls.beginDate.setValue(syncCivilStatus.postalAddress?.beginDate);
    this.postalAddressForm.controls.domiciliationType.setValue(syncCivilStatus.postalAddress?.domiciliationType);
    this.postalAddressForm.controls.domiciliationTypeComplement.setValue(syncCivilStatus.postalAddress?.domiciliationTypeComplement);
    this.postalAddressForm.controls.address.setValue(syncCivilStatus.postalAddress?.address);
    this.postalAddressForm.controls.additionalAddress.setValue(syncCivilStatus.postalAddress?.additionalAddress);
    this.postalAddressForm.controls.city.setValue(syncCivilStatus.postalAddress?.city);
    this.postalAddressForm.controls.postalCode.setValue(syncCivilStatus.postalAddress?.postalCode);
    this.civilStatusForm.updateValueAndValidity();
  }

  private async initList(): Promise<void> {
    this.filteredCredentialTypes = credentialTypesByNationality(this.rsjCivilStatus?.nationality);
    this.qpvs = await this.qpvService.findAll();
  }

  private initForm(): void {
    this.civilStatusForm = this.formBuilder.group({
      title: [{ value: this.rsjCivilStatus?.title, disabled: true }],
      surname: [{ value: this.rsjCivilStatus?.surname, disabled: true }],
      usualName: [{ value: this.rsjCivilStatus?.usualName, disabled: true }],
      firstName: [{ value: this.rsjCivilStatus?.firstName, disabled: true }],
      birthDate: [{ value: this.rsjCivilStatus?.birthDate, disabled: true }],
      ageYears: [{ value: this.rsjCivilStatus?.ageYears, disabled: true }],
      ageMonths: [{ value: this.rsjCivilStatus?.ageMonths, disabled: true }],
      nationality: [{ value: this.rsjCivilStatus?.nationality, disabled: true }],
      credentialType: [this.rsjCivilStatus?.credentialType, [Validators.required]],
      credentialDocument1: [this.rsjCivilStatus?.credentialDocument1],
      credentialDocumentDate1: [this.rsjCivilStatus?.credentialDocumentDate1],
      credentialDocument2: [this.rsjCivilStatus?.credentialDocument2],
      credentialDocumentDate2: [this.rsjCivilStatus?.credentialDocumentDate2],
      credentialDocument3: [this.rsjCivilStatus?.credentialDocument3],
      credentialDocumentDate3: [this.rsjCivilStatus?.credentialDocumentDate3],
      residentialAddressForm: this.formBuilder.group({
        id: [this.rsjCivilStatus?.residentialAddress?.id],
        beginDate: [{ value: this.rsjCivilStatus?.residentialAddress?.beginDate, disabled: true }],
        residentialStatus: [{ value: this.rsjCivilStatus?.residentialAddress?.residentialStatus, disabled: true }],
        residentialStatusComplement: [{ value: this.rsjCivilStatus?.residentialAddress?.residentialStatusComplement, disabled: true }],
        address: [{ value: this.rsjCivilStatus?.residentialAddress?.address, disabled: true }],
        additionalAddress: [{ value: this.rsjCivilStatus?.residentialAddress?.additionalAddress, disabled: true }],
        city: [{ value: this.rsjCivilStatus?.residentialAddress?.city, disabled: true }],
        postalCode: [{ value: this.rsjCivilStatus?.residentialAddress?.postalCode, disabled: true }],
        qpvId: [{ value: this.rsjCivilStatus?.residentialAddress?.qpvId, disabled: true }],
      }),
      postalAddressForm: this.formBuilder.group({
        id: [this.rsjCivilStatus?.postalAddress?.id],
        beginDate: [this.rsjCivilStatus?.postalAddress?.beginDate],
        domiciliationType: [{ value: this.rsjCivilStatus?.postalAddress?.domiciliationType, disabled: true }],
        domiciliationTypeComplement: [{ value: this.rsjCivilStatus?.postalAddress?.domiciliationTypeComplement, disabled: true }],
        address: [{ value: this.rsjCivilStatus?.postalAddress?.address, disabled: true }],
        additionalAddress: [{ value: this.rsjCivilStatus?.postalAddress?.additionalAddress, disabled: true }],
        city: [{ value: this.rsjCivilStatus?.postalAddress?.city, disabled: true }],
        postalCode: [{ value: this.rsjCivilStatus?.postalAddress?.postalCode, disabled: true }],
      }),
      addressDocument1: [this.rsjCivilStatus?.addressDocument1],
      addressDocument2: [this.rsjCivilStatus?.addressDocument2],
      sixMonthsResident: [this.rsjCivilStatus?.sixMonthsResident, Validators.requiredTrue],
      hostedFreeOfCharge: [this.rsjCivilStatus?.hostedFreeOfCharge],
      decohabitationStatus: [this.rsjCivilStatus?.decohabitationStatus],
      shelterRegistration: [this.rsjCivilStatus?.shelterRegistration],
      residentialSituationDuration: [this.rsjCivilStatus?.residentialSituationDuration],
      searchingForHousing: [this.rsjCivilStatus?.searchingForHousing],
      housingProcessStarted: [this.rsjCivilStatus?.housingProcessStarted],
      housingProcessWithHelp: [this.rsjCivilStatus?.housingProcessWithHelp],
      housingProcessWithHelpBy: [this.rsjCivilStatus?.housingProcessWithHelpBy],
    });
    this.updateCredentialDocsValidators();
    this.updateAddressDocsValidators();
    this.updateHostedByAThirdFieldsVisibility();
    this.updateCCASFieldsVisibility();
    this.updateHousingProcessWithHelpVisibility();
    this.updateHousingProcessWithHelpByVisibility();
    if (this.disabled) {
      this.civilStatusForm.disable({ emitEvent: false });
    }
  }

  private initValueChangeHandlers(): void {
    this.civilStatusForm.controls.nationality.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      if (value !== this.rsjCivilStatus?.nationality) {
        this.civilStatusForm.controls.credentialType.reset();
        this.filteredCredentialTypes = credentialTypesByNationality(value);
      }
    });
    this.civilStatusForm.controls.credentialType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetCredentialDocs();
      this.updateCredentialDocsValidators();
    });
    this.residentialAddressForm.controls.residentialStatus.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value !== this.rsjCivilStatus?.residentialAddress?.residentialStatus) {
        this.resetAddressDocs();
        this.updateAddressDocsValidators();
        this.updateHostedByAThirdFieldsVisibility();
      }
    });
    this.postalAddressForm.controls.domiciliationType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value !== this.rsjCivilStatus?.postalAddress?.domiciliationType) {
        this.updateCCASFieldsVisibility();
      }
    });
    this.civilStatusForm.controls.housingProcessStarted.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateHousingProcessWithHelpVisibility());
    this.civilStatusForm.controls.housingProcessWithHelp.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateHousingProcessWithHelpByVisibility());
    this.civilStatusForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
  }

  private resetCredentialDocs(): void {
    this.civilStatusForm.controls.credentialDocument1.reset();
    this.civilStatusForm.controls.credentialDocumentDate1.reset();
    this.civilStatusForm.controls.credentialDocument2.reset();
    this.civilStatusForm.controls.credentialDocumentDate2.reset();
    this.civilStatusForm.controls.credentialDocument3.reset();
    this.civilStatusForm.controls.credentialDocumentDate3.reset();
  }

  private resetAddressDocs(): void {
    this.civilStatusForm.controls.addressDocument1.reset();
    this.civilStatusForm.controls.addressDocument2.reset();
  }

  private updateCredentialDocsValidators(): void {
    this.updateCredentialDocValidator('credentialDocument1');
    this.updateCredentialDocValidator('credentialDocumentDate1', true);
    this.updateCredentialDocValidator('credentialDocument2');
    this.updateCredentialDocValidator('credentialDocumentDate2', true);
    this.updateCredentialDocValidator('credentialDocument3');
    this.updateCredentialDocValidator('credentialDocumentDate3', true);
  }

  private updateAddressDocsValidators(): void {
    switch (this.residentialAddressForm.controls.residentialStatus?.value) {
      case ResidentialStatus.HostedByAThird:
      case ResidentialStatus.HostedByParents:
      case ResidentialStatus.HostedByFamily:
      case ResidentialStatus.HostedByOther:
        this.residentialStatusCategory = 'HostedByAThird';
        this.civilStatusForm.controls.addressDocument1.setValidators([Validators.required]);
        this.civilStatusForm.controls.addressDocument2.setValidators([Validators.required]);
        this.civilStatusForm.setValidators([]);
        break;
      case ResidentialStatus.HostedInFosterCare:
        this.residentialStatusCategory = 'HostedInFosterCare';
        this.civilStatusForm.controls.addressDocument1.setValidators([]);
        this.civilStatusForm.controls.addressDocument2.setValidators([]);
        this.civilStatusForm.setValidators(() =>
          !this.civilStatusForm.controls.addressDocument1.value && !this.civilStatusForm.controls.addressDocument2.value
            ? { addressDocRequired: true }
            : null,
        );
        break;
      case ResidentialStatus.Tenant:
      case ResidentialStatus.Owner:
        this.residentialStatusCategory = 'TenantOwner';
        this.civilStatusForm.controls.addressDocument1.setValidators([Validators.required]);
        this.civilStatusForm.controls.addressDocument2.setValidators([]);
        this.civilStatusForm.setValidators([]);
        break;
      case ResidentialStatus.Homeless:
        this.residentialStatusCategory = 'Homeless';
        this.civilStatusForm.controls.addressDocument1.setValidators([]);
        this.civilStatusForm.controls.addressDocument2.setValidators([]);
        this.civilStatusForm.setValidators([]);
        break;
    }
    this.civilStatusForm.controls.addressDocument1.updateValueAndValidity();
    this.civilStatusForm.controls.addressDocument2.updateValueAndValidity();
    this.civilStatusForm.updateValueAndValidity();
  }

  private updateCredentialDocValidator(fieldName: string, isDate: boolean = false): void {
    this.civilStatusForm.controls[fieldName]?.setValidators(
      this.isCredentialFieldRequired(fieldName) ? (isDate ? [Validators.required, DateValidators.dateFormatValidator] : [Validators.required]) : [],
    );
    this.civilStatusForm.controls[fieldName]?.updateValueAndValidity();
  }

  private isCredentialFieldRequired(fieldName: string): boolean {
    switch (this.civilStatusForm.controls.credentialType?.value) {
      case RsjCredentialType.ValidIdentityFr:
        return ['credentialDocument1', 'credentialDocumentDate1'].includes(fieldName);
      case RsjCredentialType.ExpiredIdentityFr:
        return ['credentialDocument1', 'credentialDocumentDate1', 'credentialDocument2'].includes(fieldName);
      case RsjCredentialType.NoIdentityFr:
        return ['credentialDocument1', 'credentialDocument2'].includes(fieldName);
      case RsjCredentialType.ValidIdentityEU:
        return ['credentialDocument1', 'credentialDocumentDate1'].includes(fieldName);
      case RsjCredentialType.ResidentCard:
        return ['credentialDocument1', 'credentialDocumentDate1'].includes(fieldName);
      case RsjCredentialType.ValidRenewal:
        return ['credentialDocument1', 'credentialDocumentDate1', 'credentialDocument2', 'credentialDocumentDate2'].includes(fieldName);
      case RsjCredentialType.CJM:
        return ['credentialDocument1', 'credentialDocument2', 'credentialDocumentDate2', 'credentialDocument3', 'credentialDocumentDate3'].includes(
          fieldName,
        );
      case RsjCredentialType.Other:
        return ['credentialDocument1', 'credentialDocumentDate1', 'credentialDocument2', 'credentialDocumentDate2'].includes(fieldName);
    }
  }

  private updateHostedByAThirdFieldsVisibility(): void {
    if (
      ![
        ResidentialStatus.HostedByAThird,
        ResidentialStatus.HostedByFamily,
        ResidentialStatus.HostedByParents,
        ResidentialStatus.HostedByOther,
      ].includes(this.residentialAddressForm.controls.residentialStatus?.value)
    ) {
      this.civilStatusForm.controls.hostedFreeOfCharge.reset();
      this.civilStatusForm.controls.decohabitationStatus.reset();
    }
  }

  private updateCCASFieldsVisibility(): void {
    if ([DomiciliationType.CCAS, DomiciliationType.ApprovedAssociation].includes(this.postalAddressForm.controls.domiciliationType?.value)) {
      this.domiciliationTypeCategory = 'CCASAssociation';
    } else {
      this.domiciliationTypeCategory = 'Other';
      this.civilStatusForm.controls.shelterRegistration.reset();
      this.civilStatusForm.controls.residentialSituationDuration.reset();
      this.civilStatusForm.controls.searchingForHousing.reset();
      this.civilStatusForm.controls.housingProcessStarted.reset();
      this.civilStatusForm.controls.housingProcessWithHelp.reset();
      this.civilStatusForm.controls.housingProcessWithHelpBy.reset();
    }
  }

  private updateHousingProcessWithHelpVisibility(): void {
    if (this.civilStatusForm.controls.housingProcessStarted?.value === true) {
      this.civilStatusForm.controls.housingProcessWithHelp.enable();
    } else {
      this.civilStatusForm.controls.housingProcessWithHelp.reset();
      this.civilStatusForm.controls.housingProcessWithHelp.disable();
    }
  }

  private updateHousingProcessWithHelpByVisibility(): void {
    if (this.civilStatusForm.controls.housingProcessWithHelp?.value === true) {
      this.civilStatusForm.controls.housingProcessWithHelpBy.enable();
    } else {
      this.civilStatusForm.controls.housingProcessWithHelpBy.reset();
      this.civilStatusForm.controls.housingProcessWithHelpBy.disable();
    }
  }
}
